import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SiaActionsService } from '@sia/actions';
import {
  HtmlEditorComponent,
  SiaCssEditorComponent,
  SiaJsEditorComponent,
} from '@sia/code-editor';
import {
  SiaCheckBoxComponent,
  SiaSelectBoxComponent,
  SiaTextBoxComponent,
} from '@sia/inputs';
import {
  jsonDeepClone,
  notify,
  SiaSearchBoxComponent,
  SiaSqlQueryService,
  SiaTranslatePipe,
} from '@sia/shared';
import { firstValueFrom, Subscription } from 'rxjs';
import { SiaTemplateRendererComponent } from '../../components/template-renderer/template-renderer.component';
import { SiaTemplateExportDialogComponent } from '../../dialogs/template-export-dialog/template-export-dialog.component';
import { SiaTemplateImportDialogComponent } from '../../dialogs/template-import-dialog/template-import-dialog.component';
import {
  CssConditions,
  DataToInject,
  EventHandler,
  SiaTemplateConfiguration,
} from '../../interfaces/template-configuration';

@Component({
  selector: 'sia-template-configurator',
  standalone: true,
  imports: [
    SiaTemplateRendererComponent,
    SiaCssEditorComponent,
    SiaSelectBoxComponent,
    SiaCheckBoxComponent,
    SiaJsEditorComponent,
    HtmlEditorComponent,
    SiaTextBoxComponent,
    SiaSearchBoxComponent,
    SiaTranslatePipe,
    MatTooltipModule,
    MatDividerModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './template-configurator.component.html',
  styleUrl: './template-configurator.component.scss',
})
export class SiaTemplateConfiguratorComponent implements OnInit {
  #siaActionsService = inject(SiaActionsService);
  #dialog = inject(MatDialog);
  #siaSqlQueryService = inject(SiaSqlQueryService);

  private _template: SiaTemplateConfiguration = {
    html: ``,
    cssConditions: [],
    requiredDataFields: [],
    eventHandlers: [],
    dataToInject: [],
    viewName: undefined,
    viewSchema: undefined,
  };
  previousViewName: string | undefined;
  previousViewSchema: string | undefined;
  public get template(): SiaTemplateConfiguration {
    return this._template;
  }
  @Input() set template(value: SiaTemplateConfiguration | null) {
    this._template = value ?? {
      html: ``,
      cssConditions: [],
      requiredDataFields: [],
      eventHandlers: [],
      dataToInject: [],
      viewName: undefined,
      viewSchema: undefined,
    };
    if (
      value?.viewName != this.previousViewName ||
      value?.viewSchema != this.previousViewSchema
    ) {
      this.loadFirstRow();
      this.previousViewName = value?.viewName;
      this.previousViewSchema = value?.viewSchema;
    }
    if (value?.html) {
      this.extractElementIds(value.html);
    }
  }

  hideViewSelector = input<boolean>(false);
  selectedElement?: {
    cssConditions?: CssConditions[];
    dataToInject?: DataToInject;
    eventHandlers?: EventHandler[];
    id?: string;
  };
  #subs: Subscription[] = [];
  // Element IDs extracted from HTML
  elementIds: { id: string; description: string }[] = [];
  filteredElementIds: { id: string; description: string }[] = [];
  elementSearchExp: string | undefined;

  // Data properties extracted from data object
  dataProperties: { id: string; description: string }[] = [];

  // CSS properties
  cssProperties: { id: string; description: string }[] = [
    { id: 'style', description: 'style' },
    { id: 'class', description: 'class' },
  ];

  // Available data pipes
  dataPipes: { id: string; description: string }[] = [
    { id: 'date', description: 'Date' },
    { id: 'currency', description: 'Currency' },
    { id: 'number', description: 'Number' },
  ];
  // Available event types
  eventTypes: { id: string; description: string }[] = [
    { id: 'click', description: 'Click' },
    { id: 'dblclick', description: 'Doppio click' },
    { id: 'mouseenter', description: 'Mouse Enter' },
    { id: 'mouseleave', description: 'Mouse Leave' },
  ];

  @Output() templateChange = new EventEmitter<SiaTemplateConfiguration>();

  data: any = {};

  constructor() {
    let s0 = this.#siaActionsService.actionFired$().subscribe((action) => {
      if (action.action?.id) {
        this.manageAction(action.action?.id);
      }
    });
    this.#subs.push(s0);
  }
  onHtmlChanged($event: any) {
    this.extractElementIds($event);
    this.updateTemplate($event);
    this.templateChange.emit(this.template);
  }

  manageAction(id: string) {
    switch (id) {
      case 'import':
        this.openImportDialog();
        break;
      case 'download':
        this.exportTemplate();
        break;
      default:
        break;
    }
  }

  editElement(elementId: string) {
    if (this.selectedElement?.id == elementId) {
      this.selectedElement = undefined;
      return;
    }
    if (this.selectedElement) {
      this.saveElementConfiguration();
    }
    this.selectedElement = {
      id: elementId,
    };
    this.selectedElement.dataToInject = this.template.dataToInject?.find(
      (x) => x.elementId == elementId,
    ) || {
      elementId: elementId,
      dataField: undefined,
      useScript: false,
      dataScript: undefined,
    };
    this.selectedElement.cssConditions =
      this.template.cssConditions?.filter((x) => x.elementId == elementId) ??
      [];
    this.selectedElement.eventHandlers =
      this.template.eventHandlers?.filter((x) => x.elementId == elementId) ??
      [];
  }

  saveElementConfiguration() {
    this.template.dataToInject = this.template.dataToInject
      ? this.template.dataToInject.filter((x) => {
          return x.elementId != this.selectedElement?.id;
        })
      : [];
    this.template.dataToInject.push(
      jsonDeepClone(this.selectedElement?.dataToInject),
    );
    this.template.cssConditions = this.template.cssConditions
      ? this.template.cssConditions.filter((x) => {
          return x.elementId != this.selectedElement?.id;
        })
      : [];
    this.selectedElement?.cssConditions?.forEach((cssCondition) => {
      this.template.cssConditions?.push({
        ...cssCondition,
        elementId: this.selectedElement?.id,
      });
    });
    this.template.eventHandlers = this.template.eventHandlers
      ? this.template.eventHandlers.filter((x) => {
          return x.elementId != this.selectedElement?.id;
        })
      : [];
    this.selectedElement?.eventHandlers?.forEach((eventHandler) => {
      this.template.eventHandlers?.push({
        ...eventHandler,
        elementId: this.selectedElement?.id,
      });
    });

    // Update the template and close the panel
    this.template = { ...this.template };
    this.templateChange.emit(this.template);
    this.selectedElement = undefined;
  }

  async loadFirstRow() {
    //save on template for later import
    this.templateChange.emit(this.template);
    if (!this.template.viewSchema || !this.template.viewName) {
      this.data = {};
      return;
    }
    this.data = await this.#siaSqlQueryService.retriveTopN(
      this.template.viewSchema!,
      this.template.viewName!,
      1,
      this.template.connectionStringName ?? 'CoreDB',
    );
    this.extractDataProperties();

    if (!this.template.html?.trim()) {
      // this.#generateAutoTemplate(); // Ingestibile
    }
  }

  #generateAutoTemplate() {
    if (this.dataProperties.length === 0) return;

    const isIgnored = (f: string) =>
      f.includes('__') ||
      f.includes('filter') ||
      f.includes('search') ||
      f.includes('path') ||
      f.includes('url') ||
      f.includes('link') ||
      f.includes('img') ||
      f.includes('image') ||
      f.includes('icon') ||
      f.includes('color');

    const fields = this.dataProperties
      .map((dp) => ({
        key: dp.id,
        lower: dp.id.toLowerCase(),
      }))
      .filter((f) => !isIgnored(f.lower));

    const isDate = (f: string) =>
      f.includes('date') || f.includes('data') || f.includes('_dt');
    const isId = (f: string) =>
      f === 'id' || f.endsWith('_id') || f.endsWith('id') || f === 'pk';
    const isTitle = (f: string) =>
      f.includes('title') ||
      f.includes('titolo') ||
      f.includes('name') ||
      f.includes('nome') ||
      f.includes('subject') ||
      f.includes('oggetto');
    const isDescription = (f: string) =>
      f.includes('description') ||
      f.includes('descrizione') ||
      f.includes('note') ||
      f.includes('body') ||
      f.includes('testo');
    const isCurrency = (f: string) =>
      f.includes('amount') ||
      f.includes('price') ||
      f.includes('importo') ||
      f.includes('prezzo') ||
      f.includes('valore') ||
      f.includes('currency') ||
      f.includes('total') ||
      f.includes('totale');
    const isPercent = (f: string) =>
      f.includes('percent') || f.includes('probability');
    const isEmail = (f: string) => f.includes('email') || f.includes('mail');
    const isPhone = (f: string) =>
      f.includes('phone') || f.includes('telefono') || f.includes('tel');

    const dateFields = fields.filter((f) => isDate(f.lower));
    const idFields = fields.filter((f) => isId(f.lower));
    const titleFields = fields.filter((f) => isTitle(f.lower));
    const descFields = fields.filter((f) => isDescription(f.lower));
    const currencyFields = fields.filter((f) => isCurrency(f.lower));
    const percentFields = fields.filter((f) => isPercent(f.lower));
    const emailFields = fields.filter((f) => isEmail(f.lower));
    const phoneFields = fields.filter((f) => isPhone(f.lower));

    const categorized = new Set([
      ...dateFields,
      ...idFields,
      ...titleFields,
      ...descFields,
      ...currencyFields,
      ...percentFields,
      ...emailFields,
      ...phoneFields,
    ]);
    const otherFields = fields.filter((f) => !categorized.has(f));

    let lines: string[] = [];
    lines.push('<div class="p-1 flex flex-col gap-1" id="container">');

    // Header row: dates + currency values
    if (dateFields.length > 0 || currencyFields.length > 0) {
      lines.push('  <div class="flex flex-row justify-between items-center">');
      for (const f of dateFields) {
        lines.push(
          `    <span class="text-sm text-outline" id="${f.key}"></span>`,
        );
      }
      for (const f of currencyFields) {
        lines.push(
          `    <span class="text-sm font-semibold" id="${f.key}"></span>`,
        );
      }
      lines.push('  </div>');
    }

    // Percent fields as a progress indicator row
    for (const f of percentFields) {
      lines.push(`  <div class="text-sm font-medium" id="${f.key}"></div>`);
    }

    // Title fields: centered, prominent
    for (const f of titleFields) {
      lines.push(
        `  <div class="text-center font-semibold text-lg" id="${f.key}"></div>`,
      );
    }

    // Description fields: centered
    for (const f of descFields) {
      lines.push(`  <div class="text-center text-sm" id="${f.key}"></div>`);
    }

    // Contact info row: emails + phones
    if (emailFields.length > 0 || phoneFields.length > 0) {
      lines.push('  <div class="flex flex-row gap-2 text-sm">');
      for (const f of emailFields) {
        lines.push(
          `    <span class="ellipsis link-like" id="${f.key}"></span>`,
        );
      }
      for (const f of phoneFields) {
        lines.push(`    <span class="ellipsis" id="${f.key}"></span>`);
      }
      lines.push('  </div>');
    }

    // Other fields in a details section
    if (otherFields.length > 0) {
      lines.push('  <div class="flex flex-col gap-1 text-sm">');
      for (const f of otherFields) {
        lines.push(`    <div id="${f.key}"></div>`);
      }
      lines.push('  </div>');
    }

    // Hidden id fields
    for (const f of idFields) {
      lines.push(
        `  <div id="${f.key}" style="display: none !important"></div>`,
      );
    }

    lines.push('</div>');

    const html = lines.join('\n');
    this.template = { ...this.template, html };
    this.extractElementIds(html);

    // Auto-match all elements to data fields with pipes
    this.template.dataToInject = [];
    for (const el of this.elementIds) {
      const matched = this.#autoMatchDataField(el.id);
      if (matched) {
        this.template.dataToInject.push(matched);
      }
    }

    this.templateChange.emit(this.template);
  }
  async openImportDialog() {
    const dialogRef = this.#dialog.open(SiaTemplateImportDialogComponent, {
      width: '90vw',
      maxWidth: '100vw',
      data: JSON.stringify(this.template),
    });

    var result = await firstValueFrom(dialogRef.afterClosed());
    if (!result) {
      return;
    }
    // Update the template with the imported configuration
    this.template = JSON.parse(result);
    this.templateChange.emit(this.template);

    // Update the view and schema to previous

    await this.loadFirstRow();

    this.extractElementIds(this.template?.html ?? '');
    notify('Template importato con successo', 2000, 'success');
  }

  async exportTemplate() {
    // Placeholder for export functionality
    // This would typically generate a file for download
    this.#dialog.open(SiaTemplateExportDialogComponent, {
      width: '90vw',
      maxWidth: '100vw',
      data: JSON.stringify(this.template),
    });
  }

  async ngOnInit() {
    // Extract properties from data object
    this.extractDataProperties();
  }

  updateTemplate(newHtml: string) {
    this.template = { ...this.template, html: newHtml };
    this.templateChange.emit(this.template);
  }

  /**
   * Extract all element IDs from the HTML template
   */
  extractElementIds(htmlContent: string) {
    const idRegex = /\sid="([^"]+)"/g;
    let match;
    let foundIds = new Set<string>();

    // Find all IDs in the HTML
    while ((match = idRegex.exec(htmlContent)) !== null) {
      foundIds.add(match[1]);
    }

    const previousIds = new Set(this.elementIds.map((e) => e.id));

    // Convert to the required format and update elementIds
    this.elementIds = Array.from(foundIds).map((id) => ({
      id: id,
      description: id,
    }));
    this.filterElements(this.elementSearchExp);

    // Auto-match new element IDs to data properties
    for (const id of foundIds) {
      if (previousIds.has(id)) continue;
      if (this.template.dataToInject?.some((d) => d.elementId === id)) continue;
      const matched = this.#autoMatchDataField(id);
      if (matched) {
        if (!this.template.dataToInject) this.template.dataToInject = [];
        this.template.dataToInject.push(matched);
      }
    }
  }

  #autoMatchDataField(elementId: string): DataToInject | undefined {
    const idLower = elementId.toLowerCase();
    const match = this.dataProperties.find(
      (dp) =>
        dp.id.toLowerCase().includes(idLower) ||
        idLower.includes(dp.id.toLowerCase()),
    );
    if (!match) return undefined;

    const result: DataToInject = {
      elementId,
      dataField: match.id,
      useScript: false,
    };

    this.#applyAutoPipe(result, elementId, match.id);
    return result;
  }

  #applyAutoPipe(dto: DataToInject, elementId: string, dataField: string) {
    const combined = (elementId + ' ' + dataField).toLowerCase();
    if (combined.includes('date') || combined.includes('data')) {
      dto.pipe = 'date';
      dto.format = 'dd/MM/yyyy';
    } else if (
      combined.includes('currency') ||
      combined.includes('amount') ||
      combined.includes('price') ||
      combined.includes('importo') ||
      combined.includes('prezzo') ||
      combined.includes('valore')
    ) {
      dto.pipe = 'currency';
      dto.format = 'EUR';
    } else if (
      combined.includes('percent') ||
      combined.includes('qty') ||
      combined.includes('quantity') ||
      combined.includes('count') ||
      combined.includes('numero') ||
      combined.includes('quantita')
    ) {
      dto.pipe = 'number';
      dto.format = '1.0-2';
    }
  }

  filterElements(search: string | undefined) {
    if (!search) {
      this.filteredElementIds = this.elementIds;
      return;
    }
    const lower = search.toLowerCase();
    this.filteredElementIds = this.elementIds.filter((e) =>
      e.id.toLowerCase().includes(lower),
    );
  }

  /**
   * Extract data properties from the data object
   */
  extractDataProperties() {
    if (!this.data || typeof this.data !== 'object') {
      this.dataProperties = [];
      return;
    }
    this.dataProperties = Object.keys(this.data).map((key) => ({
      id: key,
      description: key,
    }));

    // Update current mappings
    // this.syncMappingsWithIds();
  }

  /**
   * Add a new event handler
   */
  addEventHandler() {
    if (!this.selectedElement?.id) return;
    this.selectedElement?.eventHandlers?.push({
      elementId: this.selectedElement?.id || '',
      script: undefined,
    });
  }

  /**
   * Remove an event handler
   */
  removeEventHandler(index: number) {
    if (
      !this.selectedElement?.eventHandlers ||
      index < 0 ||
      index >= this.selectedElement?.eventHandlers.length
    ) {
      return;
    }

    this.selectedElement?.eventHandlers?.splice(index, 1);
  }

  /**
   * Add a new CSS condition
   */
  addCssCondition() {
    if (!this.selectedElement?.id) return;
    this.selectedElement?.cssConditions?.push({
      elementId: this.selectedElement?.id || '',
      property: 'style',
    });
  }

  /**
   * Remove a CSS condition
   */
  removeCssCondition(index: number) {
    if (
      !this.selectedElement?.cssConditions ||
      index < 0 ||
      index >= this.selectedElement?.cssConditions.length
    ) {
      return;
    }

    this.selectedElement?.cssConditions.splice(index, 1);
    this.templateChange.emit(this.template);
  }

  ngOnDestroy() {
    this.#subs.forEach((s) => s.unsubscribe());
    this.#subs = [];
  }
}
